import { useQuery } from "@tanstack/react-query";
import rg4js from "raygun4js";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import type { Case } from "@/briefpoint-client";
import { BatchProcessingStatus } from "@/briefpoint-client/models/BatchProcessingStatus";
import { useCaseFileApi } from "@/hooks/useApi";
import { getCaseFileLatestBatchOptions } from "@/lib/query-options";
import { normalizeError } from "@/lib/utils";
import Loading from "../Loading";
import PurgatoryModal from "../PurgatoryModal/PurgatoryModal";
import BatchError from "./BatchError";
import BatchProcessingLoader from "./BatchProcessingLoader";
import CaseProductionTabs from "./CaseProductionTabs";
import ComplaintStep from "./ComplaintStep";
import ProductionStep from "./ProductionStep";
import ReviewStep from "./ReviewStep";

interface Props {
  case: Case;
}

export default function CaseProduction(props: Props) {
  const { id: caseId } = useParams<{ id: string }>();
  const caseFileApi = useCaseFileApi();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [complaintStepComplete, setComplaintStepComplete] = useState(false);
  const [productionStepComplete, setProductionStepComplete] = useState(false);
  const [isPolling, setIsPolling] = useState(true);

  // Use URL parameter as source of truth for modal state
  const isPurgatoryModalOpen = searchParams.get("review") === "true";

  const batchQueryOptions = getCaseFileLatestBatchOptions(caseFileApi, caseId);

  const batchQuery = useQuery({
    ...batchQueryOptions,
    enabled: !!caseId && isPolling,
    retry: 1,
    retryDelay: 500,
    refetchInterval: (query) => {
      if (
        query.state.data?.status === BatchProcessingStatus.NotStarted ||
        query.state.data?.status === BatchProcessingStatus.Running
      ) {
        return 3000; // Poll every 3 seconds
      }

      return false;
    },
    refetchIntervalInBackground: true,
    staleTime: 0, // Always consider data stale to ensure fresh polling
  });

  // Control polling based on the batch status.
  useEffect(() => {
    if (
      batchQuery.data?.status === BatchProcessingStatus.NotStarted ||
      batchQuery.data?.status === BatchProcessingStatus.Running
    ) {
      setIsPolling(true);
    } else {
      setIsPolling(false);
    }
  }, [batchQuery.data]);

  // Handle request errors from the batch query.
  useEffect(() => {
    if (batchQuery.error) {
      // There is a bug in CaseFileApi where it returns a SyntaxError when the batch is not found.
      // This is a Response and not an Error, so we need to check the name.
      // Also don't report errors if we are not polling to reduce duplicate error toasts.
      if (batchQuery.error?.name === "SyntaxError" || !isPolling) return;

      // Stop polling
      setIsPolling(false);

      // Report error to Raygun
      rg4js("send", {
        error: normalizeError(batchQuery.error),
      });

      // Show a toast error
      toast.error(
        "There was an error processing your documents. Please try again later.",
      );
    }
  }, [batchQuery.error, isPolling]);

  // Send processing errors to Raygun.
  useEffect(() => {
    if (batchQuery.data?.status === BatchProcessingStatus.Error) {
      rg4js("send", {
        error: normalizeError(batchQuery.data),
      });
    }
  }, [batchQuery.data]);

  const stepsComplete =
    (complaintStepComplete ? 1 : 0) + (productionStepComplete ? 1 : 0);

  const openPurgatoryModal = useCallback(() => {
    searchParams.set("review", "true");
    // Use push to allow the user to navigate to case production from the purgatory modal.
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }, [history, location.pathname, searchParams]);

  const closePurgatoryModal = useCallback(() => {
    searchParams.delete("review");

    // Use replace to prevent the user from navigating back to the purgatory modal.
    history.replace({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }, [history, location.pathname, searchParams]);

  const batchStatus = batchQuery.data?.status;

  return (
    <>
      {isPurgatoryModalOpen && (
        <PurgatoryModal
          caseTitle={(props.case.title || props.case.shortTitle) ?? ""}
          onClose={closePurgatoryModal}
        />
      )}

      <Loading isLoading={batchQuery.isLoading}>
        {batchQuery.data ? (
          batchStatus === BatchProcessingStatus.NotStarted ||
          batchStatus === BatchProcessingStatus.Running ? (
            <BatchProcessingLoader
              batch={batchQuery.data}
              onRetry={openPurgatoryModal}
            />
          ) : batchStatus === BatchProcessingStatus.Succeeded ||
            // TODO: PartialSuccess should probably show a warning to the user.
            batchStatus === BatchProcessingStatus.PartialSuccess ? (
            <CaseProductionTabs />
          ) : (
            <BatchError />
          )
        ) : (
          <>
            <ComplaintStep
              isComplete={complaintStepComplete}
              setIsComplete={(complete) => setComplaintStepComplete(complete)}
              hasWarning={!complaintStepComplete && productionStepComplete}
            />
            <ProductionStep
              isComplete={productionStepComplete}
              setIsComplete={(complete) => setProductionStepComplete(complete)}
            />
            <ReviewStep
              stepsComplete={stepsComplete}
              isLocked={stepsComplete < 2}
              openModal={openPurgatoryModal}
            />
          </>
        )}
      </Loading>
    </>
  );
}
